import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Play, Loader2, ChevronRight, Sparkles } from "lucide-react";

// -----------------------------------------------------------------------
// Shared helpers
// -----------------------------------------------------------------------
const pickRandom = (arr, n) => {
  const copy = [...arr];
  const out = [];
  for (let i = 0; i < n && copy.length; i += 1) {
    const idx = Math.floor(Math.random() * copy.length);
    out.push(copy.splice(idx, 1)[0]);
  }
  return out;
};

const OBJECT_POOL = [
  "🍎", "🚗", "🐶", "⚙️", "🌸", "📚", "🎸", "☂️",
  "🧊", "🎈", "🍋", "🪁", "🔑", "🎧", "🌵", "🧭",
  "🕰️", "🖋️", "🧵", "🥑", "🎯", "🌍", "🎲", "🪴",
];

// A tiny wrapper for reveal buttons so all 5 exercises share the style.
const AnswerBtn = ({ children, onClick, correct, wrong, disabled, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`select-none inline-flex items-center justify-center rounded-xl border text-xl sm:text-2xl min-w-[64px] min-h-[64px] px-4 py-3 transition-colors ${
      correct
        ? "border-emerald-400 bg-emerald-400/20 text-emerald-100"
        : wrong
        ? "border-red-400 bg-red-400/20 text-red-100"
        : "border-white/10 bg-white/5 hover:bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed"
    }`}
  >
    {children}
  </button>
);

const ExerciseFrame = ({ title, level, hint, children, footer, testId }) => (
  <div
    data-testid={testId}
    className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8"
  >
    <div className="flex items-center justify-between mb-2">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        {title}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        Level {level}
      </div>
    </div>
    {hint && <p className="text-sm text-zinc-300 mb-4 leading-relaxed">{hint}</p>}
    {children}
    {footer && <div className="mt-6">{footer}</div>}
  </div>
);

const ResultBanner = ({ correct, total, reactionMs, onClose }) => {
  const acc = total ? Math.round((correct / total) * 100) : 0;
  const message =
    acc >= 85
      ? "Excellent! Well done."
      : acc >= 60
      ? "Nice work — you're building consistency."
      : acc >= 40
      ? "Your performance this round was lower than usual. Try one more."
      : "Take a short break and try again.";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="exercise-result"
      className="mt-4 rounded-2xl border border-cyan-400/30 bg-cyan-400/10 p-4"
    >
      <div className="flex items-baseline gap-3">
        <div className="font-display text-3xl font-semibold text-white tabular-nums">
          {correct}/{total}
        </div>
        <div className="text-sm text-cyan-100">{acc}% accuracy</div>
        {reactionMs > 0 && (
          <div className="text-xs text-zinc-400 ml-auto">
            avg {Math.round(reactionMs)} ms
          </div>
        )}
      </div>
      <div className="mt-2 text-sm text-white leading-relaxed">{message}</div>
      {onClose && (
        <button
          data-testid="exercise-close"
          onClick={onClose}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-200 hover:text-white"
        >
          Continue <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </motion.div>
  );
};

// -----------------------------------------------------------------------
// 1. MEMORY — object recall grid
// -----------------------------------------------------------------------
export function MemoryExercise({ level = 1, onDone }) {
  const showCount = { 1: 4, 2: 6, 3: 8, 4: 8, 5: 10 }[level] || 4;
  const showMs = { 1: 4000, 2: 3200, 3: 2600, 4: 2200, 5: 1800 }[level] || 4000;
  const distract = level >= 4;

  const [phase, setPhase] = useState("intro"); // intro | study | pick | done
  const [targets, setTargets] = useState([]);
  const [choices, setChoices] = useState([]);
  const [picked, setPicked] = useState([]);
  const startAt = useRef(0);
  const [reactionMs, setReactionMs] = useState(0);

  const start = () => {
    const t = pickRandom(OBJECT_POOL, showCount);
    setTargets(t);
    // Choices = targets + distractors
    const distractors = pickRandom(
      OBJECT_POOL.filter((x) => !t.includes(x)),
      Math.min(6, OBJECT_POOL.length - t.length)
    );
    setChoices(pickRandom([...t, ...distractors], t.length + distractors.length));
    setPicked([]);
    setPhase("study");
    setTimeout(() => {
      setPhase("pick");
      startAt.current = performance.now();
    }, showMs);
  };

  const onPick = (obj) => {
    if (phase !== "pick") return;
    if (picked.includes(obj)) return;
    const next = [...picked, obj];
    setPicked(next);
    if (next.length === targets.length) {
      const rt = (performance.now() - startAt.current) / targets.length;
      setReactionMs(rt);
      const correct = next.filter((x) => targets.includes(x)).length;
      setPhase("done");
      setTimeout(() => onDone({ correct, total: targets.length, reactionMs: rt }), 400);
    }
  };

  const correctCount = picked.filter((x) => targets.includes(x)).length;

  return (
    <ExerciseFrame
      title="Memory · Object recall"
      level={level}
      hint={
        phase === "intro"
          ? `Remember ${showCount} objects, then select them from a larger grid.${
              distract ? " Extra distractor items will appear." : ""
            }`
          : phase === "study"
          ? "Memorise these objects…"
          : phase === "pick"
          ? `Select ${targets.length} objects you saw.`
          : "Reviewing your answer…"
      }
      testId="memory-exercise"
    >
      {phase === "intro" && (
        <button
          data-testid="memory-start"
          onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400"
        >
          <Play className="w-4 h-4" /> Start
        </button>
      )}
      {phase === "study" && (
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
          {targets.map((o, i) => (
            <div
              key={i}
              className="aspect-square rounded-2xl bg-violet-400/10 border border-violet-400/30 flex items-center justify-center text-3xl sm:text-4xl"
            >
              {o}
            </div>
          ))}
        </div>
      )}
      {phase === "pick" && (
        <>
          <div className="grid grid-cols-5 sm:grid-cols-7 gap-2">
            {choices.map((o) => {
              const isPicked = picked.includes(o);
              return (
                <AnswerBtn
                  key={o}
                  testId={`memory-choice-${o}`}
                  onClick={() => onPick(o)}
                  disabled={isPicked}
                  correct={isPicked && targets.includes(o)}
                  wrong={isPicked && !targets.includes(o)}
                >
                  {o}
                </AnswerBtn>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-zinc-400">
            Selected {picked.length}/{targets.length}
          </div>
        </>
      )}
      {phase === "done" && (
        <ResultBanner correct={correctCount} total={targets.length} reactionMs={reactionMs} />
      )}
    </ExerciseFrame>
  );
}

// -----------------------------------------------------------------------
// 2. ATTENTION — tap target letters ("T") while avoiding others
// -----------------------------------------------------------------------
export function AttentionExercise({ level = 1, onDone }) {
  const duration = { 1: 25000, 2: 25000, 3: 20000, 4: 20000, 5: 18000 }[level] || 25000;
  const spawnMs = { 1: 900, 2: 700, 3: 600, 4: 450, 5: 380 }[level] || 900;
  const [phase, setPhase] = useState("intro");
  const [items, setItems] = useState([]); // {id, letter, target}
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [missed, setMissed] = useState(0);
  const [reactionSum, setReactionSum] = useState(0);
  const [reactionCount, setReactionCount] = useState(0);
  const timerRef = useRef();
  const endRef = useRef();

  const start = () => {
    setItems([]);
    setCorrect(0); setWrong(0); setMissed(0);
    setReactionSum(0); setReactionCount(0);
    endRef.current = performance.now() + duration;
    setPhase("run");
  };

  useEffect(() => {
    if (phase === "run") {
      timerRef.current = setInterval(() => {
        if (performance.now() >= endRef.current) {
          clearInterval(timerRef.current);
          setPhase("done");
          return;
        }
        setItems((prev) => {
          // Age out un-tapped: after 2 spawn cycles they're a miss.
          const cutoff = performance.now() - spawnMs * 2;
          const stillAlive = prev.filter((x) => x.bornAt >= cutoff);
          const missedNow = prev.length - stillAlive.length;
          if (missedNow > 0) {
            setMissed((m) => m + prev.filter((x) => x.bornAt < cutoff && x.target).length);
          }
          const isTarget = Math.random() < 0.45;
          const nextItem = {
            id: `${performance.now()}-${Math.random()}`,
            letter: isTarget ? "T" : ["L", "F", "E", "I"][Math.floor(Math.random() * 4)],
            target: isTarget,
            bornAt: performance.now(),
          };
          return [...stillAlive.slice(-11), nextItem];
        });
      }, spawnMs);
    }

    if (phase === "done") {
      const total = correct + wrong + missed;
      const rt = reactionCount ? reactionSum / reactionCount : 0;
      setTimeout(() => onDone({ correct, total: total || 1, reactionMs: rt }), 400);
    }

    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const tap = (item) => {
    if (phase !== "run") return;
    const rt = performance.now() - item.bornAt;
    if (item.target) {
      setCorrect((c) => c + 1);
      setReactionSum((s) => s + rt);
      setReactionCount((n) => n + 1);
    } else {
      setWrong((w) => w + 1);
    }
    setItems((prev) => prev.filter((x) => x.id !== item.id));
  };

  return (
    <ExerciseFrame
      title="Attention · Target detection"
      level={level}
      hint={
        phase === "intro"
          ? `Tap only the letter T. Avoid every other letter. ${duration / 1000}s round.`
          : phase === "run"
          ? "Tap T only!"
          : "Tallying your result…"
      }
      testId="attention-exercise"
    >
      {phase === "intro" && (
        <button
          data-testid="attention-start"
          onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400"
        >
          <Play className="w-4 h-4" /> Start
        </button>
      )}
      {phase === "run" && (
        <>
          <div className="min-h-[220px] grid grid-cols-4 sm:grid-cols-6 gap-2">
            <AnimatePresence>
              {items.map((it) => (
                <motion.button
                  key={it.id}
                  data-testid={`attention-tap-${it.letter}`}
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.7, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => tap(it)}
                  className={`aspect-square rounded-xl border font-display text-3xl font-semibold ${
                    it.target
                      ? "border-cyan-400/40 bg-cyan-400/10 text-white"
                      : "border-white/10 bg-white/5 text-zinc-300"
                  }`}
                >
                  {it.letter}
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-zinc-400">
            <div>
              Hits: <span className="text-emerald-300">{correct}</span> · Misses:{" "}
              <span className="text-amber-300">{missed}</span> · Wrong taps:{" "}
              <span className="text-red-300">{wrong}</span>
            </div>
          </div>
        </>
      )}
      {phase === "done" && (
        <ResultBanner
          correct={correct}
          total={Math.max(1, correct + wrong + missed)}
          reactionMs={reactionCount ? reactionSum / reactionCount : 0}
        />
      )}
    </ExerciseFrame>
  );
}

// -----------------------------------------------------------------------
// 3. PROCESSING SPEED — match the symbol
// -----------------------------------------------------------------------
export function SpeedExercise({ level = 1, onDone }) {
  const rounds = { 1: 8, 2: 10, 3: 12, 4: 14, 5: 16 }[level] || 8;
  const choicesCount = { 1: 3, 2: 4, 3: 5, 4: 6, 5: 6 }[level] || 3;
  const [phase, setPhase] = useState("intro");
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(null);
  const [choices, setChoices] = useState([]);
  const [correct, setCorrect] = useState(0);
  const [reactionSum, setReactionSum] = useState(0);
  const [answered, setAnswered] = useState(null); // {choice, isCorrect}
  const startAt = useRef(0);

  const nextRound = (idx) => {
    if (idx >= rounds) {
      setPhase("done");
      setTimeout(
        () => onDone({ correct, total: rounds, reactionMs: reactionSum / rounds }),
        400
      );
      return;
    }
    const t = OBJECT_POOL[Math.floor(Math.random() * OBJECT_POOL.length)];
    setTarget(t);
    const distract = pickRandom(
      OBJECT_POOL.filter((x) => x !== t),
      choicesCount - 1
    );
    setChoices(pickRandom([t, ...distract], choicesCount));
    setAnswered(null);
    setRound(idx);
    startAt.current = performance.now();
  };

  const start = () => {
    setPhase("run");
    setCorrect(0);
    setReactionSum(0);
    nextRound(0);
  };

  const pick = (c) => {
    if (answered) return;
    const rt = performance.now() - startAt.current;
    const isCorrect = c === target;
    setAnswered({ choice: c, isCorrect });
    setReactionSum((s) => s + rt);
    if (isCorrect) setCorrect((v) => v + 1);
    setTimeout(() => nextRound(round + 1), 350);
  };

  return (
    <ExerciseFrame
      title="Processing speed · Symbol match"
      level={level}
      hint={
        phase === "intro"
          ? `Match the target symbol as fast as you can. ${rounds} rounds.`
          : phase === "run"
          ? `Round ${round + 1}/${rounds} — pick the matching symbol.`
          : "Reviewing…"
      }
      testId="speed-exercise"
    >
      {phase === "intro" && (
        <button
          data-testid="speed-start"
          onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400"
        >
          <Play className="w-4 h-4" /> Start
        </button>
      )}
      {phase === "run" && (
        <>
          <div className="flex items-center justify-center py-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-5xl">
              {target}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {choices.map((c) => (
              <AnswerBtn
                key={c}
                testId={`speed-choice-${c}`}
                onClick={() => pick(c)}
                disabled={!!answered}
                correct={answered && c === target && answered.choice === c}
                wrong={answered && answered.choice === c && !answered.isCorrect}
              >
                {c}
              </AnswerBtn>
            ))}
          </div>
        </>
      )}
      {phase === "done" && (
        <ResultBanner correct={correct} total={rounds} reactionMs={reactionSum / rounds} />
      )}
    </ExerciseFrame>
  );
}

// -----------------------------------------------------------------------
// 4. PROBLEM SOLVING — pattern completion
// -----------------------------------------------------------------------
export function ProblemExercise({ level = 1, onDone }) {
  const rounds = { 1: 5, 2: 6, 3: 7, 4: 8, 5: 10 }[level] || 5;

  // Simple sequence generators. Difficulty grows with step size and length.
  const makePuzzle = () => {
    const kinds = ["arith", "double", "square", "fib"];
    const kind = kinds[Math.floor(Math.random() * Math.min(kinds.length, 1 + level))];
    let seq = [];
    if (kind === "arith") {
      const step = 2 + Math.floor(Math.random() * (2 + level));
      const start = 2 + Math.floor(Math.random() * 10);
      seq = [start, start + step, start + step * 2, start + step * 3, start + step * 4];
    } else if (kind === "double") {
      const start = 1 + Math.floor(Math.random() * 4);
      seq = [start, start * 2, start * 4, start * 8, start * 16];
    } else if (kind === "square") {
      const start = 1 + Math.floor(Math.random() * 3);
      seq = [start ** 2, (start + 1) ** 2, (start + 2) ** 2, (start + 3) ** 2, (start + 4) ** 2];
    } else {
      seq = [1, 1, 2, 3, 5, 8, 13];
      const from = Math.floor(Math.random() * 3);
      seq = seq.slice(from, from + 5);
    }
    const answer = seq[seq.length - 1];
    const question = [...seq.slice(0, -1), "?"];
    const wrong = new Set();
    while (wrong.size < 3) {
      const off = (Math.floor(Math.random() * 7) - 3) || 4;
      const val = answer + off;
      if (val !== answer && val > 0) wrong.add(val);
    }
    const options = pickRandom([answer, ...wrong], 4);
    return { question, answer, options };
  };

  const [phase, setPhase] = useState("intro");
  const [round, setRound] = useState(0);
  const [puz, setPuz] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [reactionSum, setReactionSum] = useState(0);
  const [answered, setAnswered] = useState(null);
  const startAt = useRef(0);

  const next = (idx) => {
    if (idx >= rounds) {
      setPhase("done");
      setTimeout(
        () => onDone({ correct, total: rounds, reactionMs: reactionSum / rounds }),
        400
      );
      return;
    }
    setPuz(makePuzzle());
    setAnswered(null);
    setRound(idx);
    startAt.current = performance.now();
  };

  const start = () => {
    setPhase("run");
    setCorrect(0);
    setReactionSum(0);
    next(0);
  };

  const pick = (v) => {
    if (answered) return;
    const rt = performance.now() - startAt.current;
    const ok = v === puz.answer;
    setAnswered({ v, ok });
    setReactionSum((s) => s + rt);
    if (ok) setCorrect((c) => c + 1);
    setTimeout(() => next(round + 1), 500);
  };

  return (
    <ExerciseFrame
      title="Problem solving · Pattern completion"
      level={level}
      hint={
        phase === "intro"
          ? `Find the missing number in each sequence. ${rounds} rounds.`
          : phase === "run"
          ? `Round ${round + 1}/${rounds} — what comes next?`
          : "Reviewing…"
      }
      testId="problem-exercise"
    >
      {phase === "intro" && (
        <button
          data-testid="problem-start"
          onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400"
        >
          <Play className="w-4 h-4" /> Start
        </button>
      )}
      {phase === "run" && puz && (
        <>
          <div className="flex flex-wrap items-center justify-center gap-3 py-6">
            {puz.question.map((n, i) => (
              <div
                key={i}
                className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center font-display text-xl sm:text-2xl ${
                  n === "?"
                    ? "bg-emerald-400/10 border border-emerald-400/40 text-emerald-200"
                    : "bg-white/5 border border-white/10 text-white"
                }`}
              >
                {n}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {puz.options.map((o) => (
              <AnswerBtn
                key={o}
                testId={`problem-choice-${o}`}
                onClick={() => pick(o)}
                disabled={!!answered}
                correct={answered && o === puz.answer && answered.v === o}
                wrong={answered && answered.v === o && !answered.ok}
              >
                {o}
              </AnswerBtn>
            ))}
          </div>
        </>
      )}
      {phase === "done" && (
        <ResultBanner correct={correct} total={rounds} reactionMs={reactionSum / rounds} />
      )}
    </ExerciseFrame>
  );
}

// -----------------------------------------------------------------------
// 5. EXECUTIVE FUNCTION — rule switching (match by shape or colour)
// -----------------------------------------------------------------------
export function ExecutiveExercise({ level = 1, onDone }) {
  const rounds = { 1: 8, 2: 10, 3: 12, 4: 14, 5: 16 }[level] || 8;
  const SHAPES = ["●", "▲", "■", "◆"];
  const COLORS = ["#22d3ee", "#fbbf24", "#f472b6", "#34d399"];

  const makeRound = () => {
    const rule = Math.random() < 0.5 ? "shape" : "color";
    const targetShape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const targetColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    // Card shows target shape + target colour. User needs to pick a card that
    // matches by the current rule (either shape OR colour, not both).
    const options = [];
    // correct answer: matches on rule only
    if (rule === "shape") {
      options.push({
        shape: targetShape,
        color: COLORS.filter((c) => c !== targetColor)[Math.floor(Math.random() * 3)],
        correct: true,
      });
    } else {
      options.push({
        shape: SHAPES.filter((s) => s !== targetShape)[Math.floor(Math.random() * 3)],
        color: targetColor,
        correct: true,
      });
    }
    // wrong answer: matches on the OTHER attribute
    if (rule === "shape") {
      options.push({
        shape: SHAPES.filter((s) => s !== targetShape)[Math.floor(Math.random() * 3)],
        color: targetColor,
        correct: false,
      });
    } else {
      options.push({
        shape: targetShape,
        color: COLORS.filter((c) => c !== targetColor)[Math.floor(Math.random() * 3)],
        correct: false,
      });
    }
    // A neutral wrong card
    options.push({
      shape: SHAPES.filter((s) => s !== targetShape)[Math.floor(Math.random() * 3)],
      color: COLORS.filter((c) => c !== targetColor)[Math.floor(Math.random() * 3)],
      correct: false,
    });
    return {
      target: { shape: targetShape, color: targetColor },
      rule,
      options: pickRandom(options, options.length),
    };
  };

  const [phase, setPhase] = useState("intro");
  const [round, setRound] = useState(0);
  const [r, setR] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [reactionSum, setReactionSum] = useState(0);
  const [answered, setAnswered] = useState(null);
  const startAt = useRef(0);

  const next = (idx) => {
    if (idx >= rounds) {
      setPhase("done");
      setTimeout(
        () => onDone({ correct, total: rounds, reactionMs: reactionSum / rounds }),
        400
      );
      return;
    }
    setR(makeRound());
    setAnswered(null);
    setRound(idx);
    startAt.current = performance.now();
  };

  const start = () => {
    setPhase("run");
    setCorrect(0);
    setReactionSum(0);
    next(0);
  };

  const pick = (opt, i) => {
    if (answered) return;
    const rt = performance.now() - startAt.current;
    setAnswered({ i, ok: opt.correct });
    setReactionSum((s) => s + rt);
    if (opt.correct) setCorrect((c) => c + 1);
    setTimeout(() => next(round + 1), 500);
  };

  return (
    <ExerciseFrame
      title="Executive function · Rule switching"
      level={level}
      hint={
        phase === "intro"
          ? `The rule changes each round. Match by shape OR colour as instructed.`
          : phase === "run"
          ? `Round ${round + 1}/${rounds} — match the card by ${r?.rule?.toUpperCase()}.`
          : "Reviewing…"
      }
      testId="executive-exercise"
    >
      {phase === "intro" && (
        <button
          data-testid="executive-start"
          onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400"
        >
          <Play className="w-4 h-4" /> Start
        </button>
      )}
      {phase === "run" && r && (
        <>
          <div className="flex items-center justify-center py-6">
            <div
              className="w-24 h-24 rounded-3xl flex items-center justify-center text-5xl font-display border"
              style={{
                background: `${r.target.color}20`,
                borderColor: `${r.target.color}80`,
                color: r.target.color,
              }}
            >
              {r.target.shape}
            </div>
          </div>
          <div className="text-center text-xs uppercase tracking-widest text-cyan-300 font-semibold mb-3">
            Match by {r.rule}
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {r.options.map((opt, i) => (
              <button
                key={i}
                data-testid={`executive-choice-${i}`}
                disabled={!!answered}
                onClick={() => pick(opt, i)}
                className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border flex items-center justify-center text-4xl font-display transition-colors disabled:opacity-60 ${
                  answered?.i === i
                    ? opt.correct
                      ? "border-emerald-400 bg-emerald-400/20"
                      : "border-red-400 bg-red-400/20"
                    : "border-white/10 bg-white/5 hover:bg-white/10"
                }`}
                style={{ color: opt.color }}
              >
                {opt.shape}
              </button>
            ))}
          </div>
        </>
      )}
      {phase === "done" && (
        <ResultBanner correct={correct} total={rounds} reactionMs={reactionSum / rounds} />
      )}
    </ExerciseFrame>
  );
}

// -----------------------------------------------------------------------
// Registry for dynamic loading
// -----------------------------------------------------------------------
export const EXERCISE_MAP = {
  memory: MemoryExercise,
  attention: AttentionExercise,
  speed: SpeedExercise,
  problem: ProblemExercise,
  executive: ExecutiveExercise,
};
